import { ColorContext } from 'App';
import { usePerms } from 'contexts/PermsContext';
import { GetRouteData } from 'data/routeData';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaHome } from 'react-icons/fa';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa6';
import {
  LiaToggleOffSolid,
  LiaToggleOffSolid,
  LiaToggleOnSolid,
  LiaToggleOnSolid,
} from 'react-icons/lia';
import { PiVideoFill, PiWrenchFill } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { getLogo } from 'utils/imageUtils';
import styles from './assets/nav.module.css';
/**
 * @param {function} onOpenChanged a funciton listening for when the sidebar open state changes.
 * @param {string} selected the name of the screen that is currently selected. This is so we know which button to highlight as 'selected'
 * @returns
 */
export default function Sidebar({ onOpenChanged, accessLevel, updateSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useContext(ColorContext);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pageButtons, setPageButtons] = useState();
  const [selectedPage, setSelectedPage] = useState('/dashboard');
  const authContext = useContext(AuthContext);
  const { isAuthenticated } = authContext;

  const perms = usePerms();

  useEffect(() => {
    setTimeout(() => {
      const sidebarData = [...GetRouteData(perms)];
      sidebarData.forEach((item) => {
        if (item.permission()) {
          item.options.forEach((option) => {
            //set enabled by perm
            if (!option.hideSidebar) option.enabled = option.permission();

            //set active based on location
            if (window.location.pathname === option.href) item.opened = true;
          });
        } else {
          item.enabled = false;
          item.options.forEach((option) => {
            option.enabled = false;
          });
        }
      });

      setSelectedPage(window.location.pathname);
      setPageButtons(sidebarData);
    }, 1000);
  }, [isAuthenticated, updateSidebar, perms.data]);

  useEffect(() => {
    if (!location?.pathname) return;
    setSelectedPage(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) perms.reset();
  }, [isAuthenticated]);

  const toggleSidebar = () => {
    if (sidebarOpen) closeAllDropdowns();

    onOpenChanged(!sidebarOpen);
    setSidebarOpen(!sidebarOpen);
  };

  const closeAllDropdowns = () => {
    if (!sidebarOpen) {
      toggleSidebar();
    }

    const temp = pageButtons.map((item, iIndex) => {
      return { ...item, opened: false };
    });

    setPageButtons(temp);
  };

  ////////////
  // Styles (used in conditionals DO NOT TOUCH) //
  ////////////

  const ButtonStyleClosed = {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const ButtonStyleOpen = {
    backgroundColor: theme.primary,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleOpen = {
    backgroundColor: theme.primary,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  const DropButtonStyleSelected = {
    backgroundColor: theme.primaryHighlight,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingLeft: 50,
    height: 63,
    alignItems: 'center',
  };

  if (isAuthenticated && pageButtons)
    return (
      <div
        className="h-100 overflow-y-auto overflow-x-hidden bg-primary-shadow"
        style={{ width: sidebarOpen ? 300 : 65 }}
      >
        <div
          style={{
            marginBottom: 100,
          }}
        >
          {/* Icon header */}
          <div
            className="d-flex align-items-center justify-content-center bg-primary"
            style={{
              width: sidebarOpen ? /*259*/ '100%' : 65, //removed the value 259, can put it back if something looks wrong
              height: 107,
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 172,
                height: 75,
              }}
            >
              <img
                className="object-fit-fill"
                style={{ height: sidebarOpen ? '100%' : '75%' }}
                src={getLogo(sidebarOpen)}
                alt="Logo"
              />
            </div>
          </div>
          {/* Toggle menu */}
          <Button
            className="d-flex w-100 align-items-center gap-2"
            style={{ height: 63 }}
            onClick={() => toggleSidebar()}
            variant="dark"
          >
            <div className={styles['btn-icon']}>
              <IconContext.Provider value={{ size: 30 }}>
                {sidebarOpen ? <LiaToggleOnSolid /> : <LiaToggleOffSolid />}
              </IconContext.Provider>
            </div>
            {sidebarOpen && <div className="pb-2">Toggle Menu</div>}
          </Button>
          {/* Home dashboard */}
          <Button
            className="rounded-0 d-flex w-100 align-items-center gap-2"
            style={{ height: 63 }}
            onClick={() => {
              closeAllDropdowns();
              setSelectedPage('/dashboard');
              navigate('/dashboard');
            }}
            variant={selectedPage === '/dashboard' ? 'primary' : 'dark'}
          >
            <div className={styles['btn-icon']}>
              <IconContext.Provider value={{ size: 30 }}>
                <FaHome />
              </IconContext.Provider>
            </div>
            {sidebarOpen && <div className="pb-2">Dashboard</div>}
          </Button>

          {pageButtons
            .filter(
              (_, index) => index <= pageButtons.findIndex((page) => page.name === 'Public Info'),
            )
            .map((dropdown, index) => {
              if (dropdown.enabled)
                return (
                  <div key={index}>
                    <button
                      style={
                        dropdown.options
                          .map((option) => {
                            return selectedPage === option.href;
                          })
                          .includes(true)
                          ? { ...DropButtonStyleSelected, paddingLeft: 0 }
                          : dropdown.opened
                            ? ButtonStyleOpen
                            : ButtonStyleClosed
                      }
                      onClick={() => {
                        const temp = pageButtons.map((item, iIndex) => {
                          if (iIndex === index) {
                            return { ...item, opened: !item.opened };
                          } else {
                            return { ...item, opened: false };
                          }
                        });

                        setPageButtons(temp);
                        if (!sidebarOpen) toggleSidebar();
                      }}
                    >
                      <div className="d-flex justify-content-between w-100">
                        <div className="d-flex align-items-center">
                          <div className="mx-3">{dropdown.icon()}</div>
                          {sidebarOpen && <div>{dropdown.name}</div>}
                        </div>
                        {sidebarOpen && (
                          <div className="me-3">
                            {dropdown.opened ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        )}
                      </div>
                    </button>
                    {dropdown.opened && sidebarOpen && (
                      <div>
                        {dropdown.options.map((option, index) => {
                          if (option.enabled)
                            return (
                              <Button
                                className="pe-0 py-0 rounded-0"
                                key={index}
                                style={
                                  selectedPage === option.href
                                    ? DropButtonStyleSelected
                                    : DropButtonStyleOpen
                                }
                                onClick={() => {
                                  setSelectedPage(option.href);
                                  navigate(option.href);
                                }}
                                variant={selectedPage === option.href ? 'primary' : 'secondary'}
                              >
                                <div className="d-flex align-items-center">
                                  <div className="me-3" style={{ width: 30 }}>
                                    {option.icon()}
                                  </div>
                                  <div>{option.name}</div>
                                </div>
                              </Button>
                            );
                        })}
                      </div>
                    )}
                  </div>
                );
            })}

          {/* ExacqVision VMS */}
          {perms.testPermission(
            [8, 9, 10, 11, 12, 13],
            ['ViewExacqVisionOverwatchVMSConnections'],
          ) && (
            <Button
              className="rounded-0 d-flex gap-2 w-100 align-items-center"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                setSelectedPage('/vms');
                navigate('/vms');
              }}
              variant={selectedPage === '/vms' ? 'primary' : 'dark'}
            >
              <div className={styles['btn-icon']}>
                <IconContext.Provider value={{ size: 35 }}>
                  <PiVideoFill />
                </IconContext.Provider>
              </div>
              {sidebarOpen && <div className="pb-2">Overwatch VMS</div>}
            </Button>
          )}

          {/* Maintenance Dashboard*/}
          {perms.maintenanceLevel > 0 && (
            <Button
              className="rounded-0 d-flex gap-2 w-100 align-items-center"
              style={{ height: 63 }}
              onClick={() => {
                closeAllDropdowns();
                setSelectedPage('/maintenancedashboard');
                navigate('/maintenancedashboard');
              }}
              variant={selectedPage === '/maintenancedashboard' ? 'primary' : 'dark'}
            >
              <div className={styles['btn-icon']}>
                <IconContext.Provider value={{ size: 35 }}>
                  <PiWrenchFill />
                </IconContext.Provider>
              </div>

              {sidebarOpen && <div className="pb-2">Maintenance Devices</div>}
            </Button>
          )}

          {pageButtons
            .filter(
              (_, index) => index > pageButtons.findIndex((page) => page.name === 'Public Info'),
            )
            .map((dropdown, index) => {
              index += 1 + pageButtons.findIndex((page) => page.name === 'Public Info');

              if (dropdown.enabled)
                return (
                  <div key={index}>
                    <button
                      style={
                        dropdown.options
                          .map((option) => {
                            return selectedPage === option.href;
                          })
                          .includes(true)
                          ? { ...DropButtonStyleSelected, paddingLeft: 0 }
                          : dropdown.opened
                            ? ButtonStyleOpen
                            : ButtonStyleClosed
                      }
                      onClick={() => {
                        const temp = pageButtons.map((item, iIndex) => {
                          if (iIndex === index) {
                            return { ...item, opened: !item.opened };
                          } else {
                            return { ...item, opened: false };
                          }
                        });

                        setPageButtons(temp);
                        if (!sidebarOpen) toggleSidebar();
                      }}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <div className="d-flex align-items-center">
                          <div className="mx-3">{dropdown.icon()}</div>
                          {sidebarOpen && <div>{dropdown.name}</div>}
                        </div>
                        {sidebarOpen && (
                          <div className="me-3">
                            {dropdown.opened ? <FaChevronUp /> : <FaChevronDown />}
                          </div>
                        )}
                      </div>
                    </button>
                    {dropdown.opened && sidebarOpen && (
                      <div>
                        {dropdown.options.map((option, index) => {
                          if (option.enabled)
                            return (
                              <Button
                                className="pe-0 py-0 rounded-0"
                                style={
                                  selectedPage === option.href
                                    ? DropButtonStyleSelected
                                    : DropButtonStyleOpen
                                }
                                key={index}
                                onClick={() => {
                                  setSelectedPage(option.href);
                                  navigate(option.href);
                                }}
                                variant={selectedPage === option.href ? 'primary' : 'secondary'}
                              >
                                <div className="d-flex align-items-center">
                                  <div className="me-3" style={{ width: 30 }}>
                                    {option.icon()}
                                  </div>
                                  <div>{option.name}</div>
                                </div>
                              </Button>
                            );
                        })}
                      </div>
                    )}
                  </div>
                );
            })}
        </div>
      </div>
    );
}
